import {
  RDP_PORT,
  RDP_RULE_DESCRIPTION,
  type Server,
  type TerminalReachability,
} from "@cloudserver/contracts";
import { Check, Copy, ShieldCheck, ShieldAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAllowTerminal, useTerminalReachability, useTerminalSetup } from "../api/terminal.js";
import { useProjectAccess } from "../api/users.js";
import { ErrorMessage } from "../ui/feedback.js";
import { relativeTime } from "../ui/format.js";
import { Button, Card } from "../ui/primitives.js";
import { useToast } from "../ui/toast.js";
import { useServer } from "../api/servers.js";
import { SendToSession } from "./send.js";

/** The one-line CA install command with copy and "type it in the console". */
export function InstallCommand({
  projectId,
  serverId,
  command,
}: {
  projectId: string;
  serverId: string;
  command: string;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const server = useServer(projectId, serverId).data;
  const copy = async () => {
    await navigator.clipboard.writeText(command).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium">{t("terminal.setup.command")}</p>
      <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs">
        {command}
      </pre>
      <p className="text-xs text-muted">{t("terminal.setup.commandHint")}</p>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          icon={
            copied ? (
              <Check className="size-4 text-ok" aria-hidden />
            ) : (
              <Copy className="size-4" aria-hidden />
            )
          }
          onClick={() => void copy()}
        >
          {copied ? t("terminal.setup.copied") : t("terminal.setup.copy")}
        </Button>
        <SendToSession
          projectId={projectId}
          server={server}
          text={command}
          open="console"
          label={t("terminal.setup.typeInConsole")}
        />
      </div>
    </div>
  );
}

/**
 * Port 22 through the server's firewalls, with "allow from the panel's
 * address"; with `kind="rdp"` the remote desktop's port 3389, and a warning
 * when it is open to the whole internet.
 */
export function ReachabilityNote({
  projectId,
  reach,
  kind = "ssh",
}: {
  projectId: string;
  reach: TerminalReachability;
  kind?: "ssh" | "rdp";
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const allow = useAllowTerminal(projectId);
  const texts = kind === "rdp" ? "rdp.reach" : "terminal.reach";
  const blocking = reach.firewalls.filter((f) => !f.allows);
  const target = blocking[0];
  const ips = reach.egressIps.join(", ");
  const text =
    reach.verdict === "open"
      ? t(`${texts}.open`)
      : reach.verdict === "blocked"
        ? t(`${texts}.blocked`, { names: blocking.map((f) => f.name).join(", ") })
        : reach.verdict === "unknown"
          ? t(`${texts}.unknown`)
          : t(`${texts}.noAddress`);
  const exposed = kind === "rdp" && reach.exposed && reach.verdict !== "no_address";
  const Icon = reach.verdict === "open" && !exposed ? ShieldCheck : ShieldAlert;
  return (
    <div className="flex flex-col gap-2" role="status">
      <p className="flex items-start gap-2 text-sm">
        <Icon
          className={
            reach.verdict === "open" && !exposed
              ? "mt-0.5 size-4 text-ok"
              : "mt-0.5 size-4 text-warn"
          }
          aria-hidden
        />
        <span>
          <span className="font-medium">{t(`${texts}.title`)}: </span>
          {text}
        </span>
      </p>
      {exposed && <p className="text-sm text-warn">{t("rdp.reach.exposed")}</p>}
      {ips && <p className="text-xs text-muted">{t("terminal.reach.egress", { ips })}</p>}
      {reach.verdict === "blocked" && target && (
        <div className="flex flex-col gap-1">
          <Button
            variant="secondary"
            className="self-start"
            busy={allow.isPending}
            onClick={() =>
              allow.mutate(
                {
                  firewallId: target.id,
                  egressIps: reach.egressIps,
                  ...(kind === "rdp" ? { port: RDP_PORT, description: RDP_RULE_DESCRIPTION } : {}),
                },
                {
                  onSuccess: () =>
                    toast({
                      tone: "ok",
                      title: t("terminal.reach.allowed", { name: target.name }),
                    }),
                },
              )
            }
          >
            {t("terminal.reach.allow")}
          </Button>
          <p className="text-xs text-muted">
            {t(`${texts}.allowHint`, { name: target.name, ips })}
          </p>
          {allow.error && <ErrorMessage error={allow.error} />}
        </div>
      )}
    </div>
  );
}

/**
 * "Browser terminal" on the server page: whether certificate sign-in works,
 * the one-line command that makes the server trust the CA, the host key we
 * know and whether port 22 is reachable. Not for guests.
 */
export function TerminalSetupCard({ projectId, server }: { projectId: string; server: Server }) {
  const { t, i18n } = useTranslation();
  const access = useProjectAccess(projectId);
  const allowed = access === "owner" || access === "admin";
  const setup = useTerminalSetup(projectId, server.id, allowed);
  const reach = useTerminalReachability(projectId, server.id, allowed);
  if (!allowed) return null;
  return (
    <Card className="flex flex-col gap-4 lg:col-span-2">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold">{t("terminal.setup.title")}</h2>
        <p className="text-sm text-muted">{t("terminal.setup.intro")}</p>
      </div>
      {setup.error ? (
        <ErrorMessage error={setup.error} />
      ) : setup.data ? (
        <>
          <p className="text-sm">
            {setup.data.certificateWorkedAt
              ? t("terminal.setup.trusted", {
                  time: relativeTime(setup.data.certificateWorkedAt, i18n.language),
                })
              : t("terminal.setup.notYet")}
          </p>
          {setup.data.certificateWorkedAt ? (
            // Still needed after a CA rotation: run it again to install the new key.
            <details className="text-sm">
              <summary className="cursor-pointer font-medium">
                {t("terminal.setup.command")}
              </summary>
              <div className="pt-2">
                <InstallCommand
                  projectId={projectId}
                  serverId={server.id}
                  command={setup.data.installCommand}
                />
              </div>
            </details>
          ) : (
            <InstallCommand
              projectId={projectId}
              serverId={server.id}
              command={setup.data.installCommand}
            />
          )}
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-medium">{t("terminal.setup.hostKey")}</p>
            <p className="break-all font-mono text-xs text-muted">
              {setup.data.hostKeyFingerprint ?? t("terminal.setup.hostKeyUnknown")}
            </p>
          </div>
        </>
      ) : null}
      {reach.data && <ReachabilityNote projectId={projectId} reach={reach.data} />}
    </Card>
  );
}
